import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import {
    Typography,
    Skeleton,
    Box,
    Stack,
    Grid,
    Chip,
    Paper,
    Button,
    Link,
    SxProps,
    Theme,
    TextField,
} from '@mui/material'
import useOrgPage from './useOrgPage'
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
} from 'recharts'
import React from 'react'
import DatePicker from '@components/DatePicker'
import { GroupWithCount, HabitatAdminUser } from '@types'
import { Licence } from '@types'
import {
    DataGridPro,
    getGridStringOperators,
    GridColDef,
    GridColumnGroupingModel,
    GridRowsProp,
    GridToolbar,
} from '@mui/x-data-grid-pro'
import useDataGridSearch from '@hooks/useDataGridSearch'
import dayjs from 'dayjs'

const OrgPage = () => {
    const {
        form,
        org,
        users,
        sessionsChartData,
        ordersChartData,
        sessions,
        orders,
        licences,
        loadingOrg,
        loadingUsers,
        loadingSessions,
        loadingLicences,
        error,
        downloadReport,
    } = useOrgPage()

    if (error) {
        return <DashboardLayout>{error}</DashboardLayout>
    }

    if (!org && !loadingOrg) {
        return <DashboardLayout>Organization not found.</DashboardLayout>
    }

    let createdDate = ''
    let address = ''
    let licenceInitialBillDate = ''

    if (org) {
        createdDate = new Date(org.created_date).toLocaleDateString()
        address = `${org.address}, ${org.city}, ${org.state}, ${org.country}, ${org.postcode}`
        licenceInitialBillDate = org.licence_initial_bill_date ?? ''
    }

    let infoItems = [
        {
            label: 'Address',
            value: address,
        },
        {
            label: 'Date created',
            value: createdDate,
        },
        {
            label: 'Licence initial bill date',
            value: dayjs(licenceInitialBillDate).isValid()
                ? dayjs(licenceInitialBillDate).format('YYYY-MM-DD')
                : '-',
        },
    ]

    if (org?.department) {
        infoItems = [
            {
                label: 'Department',
                value: org.department,
            },
            ...infoItems,
        ]
    }

    return (
        <DashboardLayout>
            {!loadingOrg && org ? (
                <Stack
                    flexDirection={'row'}
                    gap={2}
                    alignItems={'center'}
                    mb={2}
                >
                    <Typography variant="h4">{org.name}</Typography>
                    <Chip
                        color={org.active ? 'success' : 'error'}
                        label={org.active ? 'active' : 'inactive'}
                    />
                </Stack>
            ) : (
                <Stack>
                    <Skeleton variant="text" width={360} height={50} />
                </Stack>
            )}
            {!loadingOrg && org ? (
                <>
                    <Paper sx={{ height: '100%', marginBottom: '16px' }}>
                        <InfoList items={infoItems} />
                    </Paper>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={6} md={3}>
                            {loadingUsers ? (
                                <Skeleton
                                    variant="rectangular"
                                    width={'100%'}
                                    height={'100%'}
                                ></Skeleton>
                            ) : (
                                <InfoCard
                                    label="Total users"
                                    count={users.length}
                                />
                            )}
                        </Grid>
                        <Grid item xs={6} md={3}>
                            {loadingUsers ? (
                                <Skeleton
                                    variant="rectangular"
                                    width={'100%'}
                                    height={'100%'}
                                ></Skeleton>
                            ) : (
                                <InfoCard
                                    label="Active users"
                                    count={users.filter((u) => u.active).length}
                                />
                            )}
                        </Grid>
                        <Grid item xs={6} md={3}>
                            {loadingSessions ? (
                                <Skeleton
                                    variant="rectangular"
                                    width={'100%'}
                                    height={'100%'}
                                ></Skeleton>
                            ) : (
                                <InfoCard
                                    label="Total sesions"
                                    count={sessions.length}
                                />
                            )}
                        </Grid>
                        <Grid item xs={6} md={3}>
                            {loadingSessions ? (
                                <Skeleton
                                    variant="rectangular"
                                    width={'100%'}
                                    height={'100%'}
                                ></Skeleton>
                            ) : (
                                <InfoCard
                                    label="Total orders"
                                    count={orders.length}
                                />
                            )}
                        </Grid>
                    </Grid>
                </>
            ) : (
                <Stack mb={2}>
                    <Skeleton
                        variant="rectangular"
                        width={'100%'}
                        height={240}
                    />
                </Stack>
            )}

            {!loadingSessions ? (
                <>
                    <Grid container spacing={2} mt={4}>
                        <Grid item xs={6} md={6}>
                            <Stack
                                flexDirection={'row'}
                                alignItems={'center'}
                                gap={1}
                            >
                                <Typography variant="h6" whiteSpace={'nowrap'}>
                                    Showing results between
                                </Typography>
                                <DatePicker
                                    label="From Date"
                                    value={form.values.fromDate}
                                    maxDate={form.values.toDate}
                                    onChange={(date) =>
                                        form.setValue('fromDate', date)
                                    }
                                    sx={{ width: 140 }}
                                />
                                <Typography variant="h6">and</Typography>
                                <DatePicker
                                    label="To Date"
                                    value={form.values.toDate}
                                    minDate={form.values.fromDate}
                                    onChange={(date) =>
                                        form.setValue('toDate', date)
                                    }
                                    sx={{ width: 140 }}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <Stack
                                justifyContent={'center'}
                                alignItems={'flex-end'}
                                height={'100%'}
                            >
                                <Button
                                    variant="contained"
                                    onClick={downloadReport}
                                >
                                    Download Report
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} mb={2} mt={2}>
                        <Grid item xs={6} md={6}>
                            <InfoCard
                                count={
                                    sessionsChartData.filteredSessions.length
                                }
                                label={'Sessions'}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <InfoCard
                                count={ordersChartData.filteredOrders.length}
                                label={'Orders'}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} mb={2} mt={2}>
                        <Grid item xs={12} md={6}>
                            <ChartBox
                                title={'Sessions over time'}
                                data={sessionsChartData.sessionsOverTime}
                                dataKey="sessions"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ChartBox
                                title={'Orders over time'}
                                data={ordersChartData.ordersOverTime}
                                dataKey="orders"
                            />
                        </Grid>
                    </Grid>
                </>
            ) : (
                <Skeleton variant="rounded" width="100%" height={400} />
            )}

            {!loadingOrg && org ? (
                <Paper sx={{ padding: 1 }}>
                    <Stack direction={'column'} minHeight={250} maxHeight={500}>
                        <Typography variant="h5">Groups</Typography>
                        <GroupsTable groups={org.groups} />
                    </Stack>
                </Paper>
            ) : (
                <Stack mb={2}>
                    <Skeleton
                        variant="rectangular"
                        width={'100%'}
                        height={240}
                    />
                </Stack>
            )}

            {!loadingUsers ? (
                <>
                    <Paper sx={{ padding: 1, marginTop: 2 }}>
                        <UserTable
                            label="Users"
                            users={users}
                            sx={{
                                height: 520,
                                marginTop: 1,
                                marginBottom: 2,
                            }}
                        />
                    </Paper>
                </>
            ) : (
                <Skeleton variant="rounded" width="100%" height={400} />
            )}

            {!loadingLicences && !loadingUsers ? (
                <>
                    <Paper sx={{ padding: 1, marginTop: 2 }}>
                        <LicencesTable
                            label="Licences"
                            licences={licences}
                            users={users ? users : []}
                            loading={loadingLicences}
                            sx={{
                                height: 520,
                                marginTop: 1,
                            }}
                        />
                    </Paper>
                </>
            ) : (
                <Skeleton variant="rounded" width="100%" height={400} />
            )}
        </DashboardLayout>
    )
}

export default OrgPage

interface InfoItem {
    label: string
    value: React.ReactNode
}

interface InfoListProps {
    items: InfoItem[] | false
}

const InfoList = ({ items }: InfoListProps) => {
    if (!items) {
        return null
    }
    return (
        <Stack
            spacing={1}
            padding={2}
            sx={{ height: '100%' }}
            bgcolor={'white'}
        >
            {items.map(({ label, value }, index) => (
                <Box
                    key={index}
                    // display="flex"
                    // alignItems="center"
                    sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: '250px 1fr',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        // sx={{ minWidth: 120, flexShrink: 0 }}
                    >
                        {label}
                    </Typography>
                    <Typography variant="body1">{value}</Typography>
                </Box>
            ))}
        </Stack>
    )
}

const InfoCard = ({
    count,
    label,
}: {
    count: number | string
    label: string
}) => {
    return (
        <Paper
            sx={{
                padding: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <Typography variant="body1">{label}</Typography>
            <Typography
                variant="h4"
                fontWeight="bold"
                component="div"
                sx={{ fontSize: '2rem' }}
            >
                {count}
            </Typography>
        </Paper>
    )
}

interface ChartBoxProps {
    title: string
    dataKey: string
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    data: any
}

const ChartBox = ({ title, dataKey, data }: ChartBoxProps) => {
    return (
        <Paper sx={{ width: '100%' }}>
            <Stack width={'100%'} padding={2}>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                <Box display="flex" alignItems="center">
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={data}>
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Line
                                type="monotone"
                                dataKey={dataKey}
                                stroke="#8884d8"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Stack>
        </Paper>
    )
}

interface UserTableProps {
    label: string
    users: HabitatAdminUser[]
    sx?: SxProps<Theme>
}

interface LicencesTableProps {
    label: string
    licences: Licence[]
    users: HabitatAdminUser[]
    loading: boolean
    sx?: SxProps<Theme>
}

const filteredOperators = getGridStringOperators().filter(
    (operator) => operator.value !== 'isAnyOf'
)

const UserTable = ({ label, users, sx }: UserTableProps) => {
    const { searchText, requestSearch, filterModel } = useDataGridSearch()

    if (!users) {
        return <></>
    }

    const columns: GridColDef<GridRowsProp[number]>[] = [
        // These fields are always hidden, but are used for filtering and searching
        {
            field: 'id',
            headerName: 'ID',
            width: 0,
            hideable: false,
            filterOperators: filteredOperators,
        },
        {
            field: 'first_name',
            headerName: 'First Name',
            width: 0,
            hideable: false,
            sortable: true,
            filterOperators: filteredOperators,
        },
        {
            field: 'last_name',
            headerName: 'Last Name',
            width: 0,
            hideable: false,
            filterOperators: filteredOperators,
        },
        // Composite field of `id`, `first_name`, and `last_name` in a compact format
        {
            field: 'nameId',
            filterable: false,
            headerName: 'Name',
            width: 300,
            display: 'flex',
            renderCell: (params) => {
                return (
                    <div>
                        <p>
                            {params.row['first_name']} {params.row['last_name']}
                        </p>
                        <Link
                            href={`/user-deepdive?q=${params.row['id']}`}
                            style={{ fontSize: '0.9em', color: 'darkgray' }}
                        >
                            {params.row['id']}
                        </Link>
                    </div>
                )
            },
            renderHeader: () => {
                return (
                    <div
                        style={{
                            fontWeight: 'var(--unstable_DataGrid-headWeight)',
                            textAlign: 'left',
                        }}
                    >
                        <p>Name</p>
                        <p style={{ fontSize: '0.9em', color: 'darkgray' }}>
                            Account ID
                        </p>
                    </div>
                )
            },
            valueGetter: (_, row) => {
                return `${row['first_name']} ${row['last_name']} | ${row['id']}`
            },
            sortComparator: (v1, v2) => {
                return v1 > v2 ? 1 : -1
            },
            filterOperators: filteredOperators,
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 250,
            filterOperators: filteredOperators,
        },
        {
            field: 'role',
            headerName: 'Role',
            width: 250,
            filterOperators: filteredOperators,
        },
    ]

    return (
        <Box sx={sx}>
            <Stack direction={'row'} sx={{ marginBottom: 1 }}>
                <Typography variant="h6">{label}</Typography>
                <TextField
                    variant="standard"
                    value={searchText}
                    onChange={(event) => requestSearch(event.target.value)}
                    placeholder="Search…"
                    sx={{ marginLeft: 2 }}
                />
            </Stack>
            <DataGridPro
                pagination
                paginationMode="client"
                rows={users}
                columns={columns}
                slotProps={{
                    toolbar: {
                        csvOptions: { disableToolbarButton: true }, // Disable the default export button
                        printOptions: { disableToolbarButton: true }, // Disable print button if needed
                    },
                }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                            first_name: false,
                            last_name: false,
                        },
                    },
                }}
                pageSizeOptions={[10, 20, 50]}
                disableRowSelectionOnClick
                filterModel={filterModel}
            />
        </Box>
    )
}

interface GroupsTableProps {
    groups: GroupWithCount[]
}

interface GroupsTableProps {
    groups: GroupWithCount[]
}

const GroupsTable = ({ groups }: GroupsTableProps): JSX.Element => {
    const { searchText, requestSearch, filterModel } = useDataGridSearch()

    const cols: GridColDef<GroupWithCount>[] = [
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 400,
            renderCell: (params) => (
                <div>
                    <Typography variant="body1">{params.row.name}</Typography>
                    <Link href={`/group/${params.row.id}`}>
                        <Typography variant="subtitle1">
                            {params.row.id}
                        </Typography>
                    </Link>
                </div>
            ),
        },
        {
            field: 'userCount',
            headerName: 'User Count',
            minWidth: 150,
        },
    ]

    return (
        <Box>
            <TextField
                variant="standard"
                value={searchText}
                onChange={(e) => requestSearch(e.target.value)}
                placeholder="Search..."
                sx={{ marginLeft: 2 }}
            />
            <DataGridPro
                pagination
                paginationMode="client"
                rows={groups}
                columns={cols}
                slotProps={{
                    toolbar: {
                        csvOptions: { disableToolbarButton: true }, // Disable the default export button
                        printOptions: { disableToolbarButton: true }, // Disable print button if needed
                    },
                }}
                pageSizeOptions={[10, 20, 50]}
                disableRowSelectionOnClick
                filterModel={filterModel}
            />
        </Box>
    )
}

type LicenceRow = Licence & {
    assignmentUserName: string
    assignmentUserEmail: string
    assignmentUserId: string
    assignmentFromDate: string | null
    assignmentToDate: string | null
}

const formatDateTime = (value: string | null | undefined) => {
    if (!value) {
        return '-'
    }

    const formatted = dayjs(value)
    return formatted.isValid() ? formatted.format('YYYY-MM-DD HH:mm') : '-'
}

const formatDate = (value: string | null | undefined) => {
    if (!value) {
        return '-'
    }

    const formatted = dayjs(value)
    return formatted.isValid() ? formatted.format('YYYY-MM-DD') : '-'
}

const LicencesTable = ({
    label,
    licences,
    users,
    loading,
}: LicencesTableProps) => {
    const userMap: Map<string, { name: string; email: string }> =
        users && users?.length
            ? new Map(
                  users.map((user) => [
                      user.id,
                      {
                          name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
                          email: user.email ?? '',
                      },
                  ])
              )
            : new Map<string, { name: string; email: string }>()

    const rows: LicenceRow[] = licences.length
        ? licences?.map((licence) => {
              const assignedUserId = licence.activeAssignment?.user_id ?? ''
              const assignedUser = userMap.get(assignedUserId)

              return {
                  ...licence,
                  assignmentUserId: assignedUserId,
                  assignmentUserName:
                      assignedUser?.name || assignedUserId || 'Unassigned',
                  assignmentUserEmail: assignedUser?.email || '-',
                  assignmentFromDate:
                      licence.activeAssignment?.from_date ?? null,
                  assignmentToDate: licence.activeAssignment?.to_date ?? null,
              }
          })
        : []

    const columns: GridColDef<LicenceRow>[] = [
        {
            field: 'id',
            headerName: 'Licence ID',
            minWidth: 240,
            filterOperators: filteredOperators,
        },
        {
            field: 'tierName',
            headerName: 'Tier',
            minWidth: 140,
            valueGetter: (_value, row) => row.tier?.name ?? '-',
            filterOperators: filteredOperators,
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 120,
            filterOperators: filteredOperators,
        },
        {
            field: 'billing_frequency',
            headerName: 'Billing',
            minWidth: 120,
            filterOperators: filteredOperators,
        },
        {
            field: 'trial',
            headerName: 'Trial',
            minWidth: 100,
            valueFormatter: (value) => (Number(value) === 1 ? 'Yes' : 'No'),
            filterOperators: filteredOperators,
        },
        {
            field: 'from_date',
            headerName: 'From',
            minWidth: 120,
            valueFormatter: (value) => formatDate(value as string | null),
        },
        {
            field: 'to_date',
            headerName: 'To',
            minWidth: 120,
            valueFormatter: (value) => formatDate(value as string | null),
        },
        {
            field: 'purchased_at',
            headerName: 'Purchased',
            minWidth: 170,
            valueFormatter: (value) => formatDateTime(value as string | null),
        },
        {
            field: 'assignmentUserName',
            headerName: 'Assigned User',
            minWidth: 180,
            filterOperators: filteredOperators,
        },
        {
            field: 'assignmentUserEmail',
            headerName: 'Assigned Email',
            minWidth: 220,
            filterOperators: filteredOperators,
        },
        {
            field: 'assignmentUserId',
            headerName: 'Assigned User ID',
            minWidth: 240,
            filterOperators: filteredOperators,
        },
        {
            field: 'assignmentFromDate',
            headerName: 'From',
            minWidth: 120,
            valueFormatter: (value) => formatDate(value as string | null),
        },
        {
            field: 'assignmentToDate',
            headerName: 'To',
            minWidth: 120,
            valueFormatter: (value) => formatDate(value as string | null),
        },
    ]

    const columnGroupingModel: GridColumnGroupingModel = [
        {
            groupId: 'assignment',
            headerName: 'Assignment',
            children: [
                { field: 'assignmentUserName' },
                { field: 'assignmentUserEmail' },
                { field: 'assignmentUserId' },
                { field: 'assignmentFromDate' },
                { field: 'assignmentToDate' },
            ],
        },
    ]

    return (
        <Box>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
                {label}
            </Typography>
            <DataGridPro
                pagination
                paginationMode="client"
                loading={loading}
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        printOptions: { disableToolbarButton: true },
                    },
                }}
                pageSizeOptions={[10, 20, 50]}
                disableRowSelectionOnClick
            />
        </Box>
    )
}
